/**
 * React Query hooks for CSV Import API
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as csvImportApi from '../services/csvImportApi'
import type {
  CSVUploadResponse,
  CSVImportExecuteParams,
  CSVImportResponse,
  ImportTemplateInfo,
  ImportTemplateDetail,
  ImportTemplateCreate,
  EntityLevel,
  TargetTable,
  ExistingDataCheckResponse,
  ImportedDatasetInfo,
  ImportedValuesResponse,
} from '../types'

// ============================================================================
// Upload
// ============================================================================

export function useUploadCSV() {
  return useMutation<
    CSVUploadResponse,
    Error,
    { file: File }
  >({
    mutationFn: ({ file }) =>
      csvImportApi.uploadCSV(file),
  })
}

// ============================================================================
// Templates
// ============================================================================

export function useCSVTemplates(entityLevel?: EntityLevel) {
  return useQuery<ImportTemplateInfo[]>({
    queryKey: ['csv-import', 'templates', entityLevel],
    queryFn: () =>
      csvImportApi.getTemplates(entityLevel ? { entityLevel } : undefined),
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useCSVTemplate(id: number | null) {
  return useQuery<ImportTemplateDetail>({
    queryKey: ['csv-import', 'templates', id],
    queryFn: () => csvImportApi.getTemplate(id!),
    enabled: id !== null,
    staleTime: 60 * 1000,
  })
}

export function useCreateCSVTemplate() {
  const queryClient = useQueryClient()

  return useMutation<ImportTemplateInfo, Error, ImportTemplateCreate>({
    mutationFn: csvImportApi.createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['csv-import', 'templates'],
      })
    },
  })
}

export function useUpdateCSVTemplate() {
  const queryClient = useQueryClient()

  return useMutation<
    ImportTemplateInfo,
    Error,
    { id: number; update: Partial<ImportTemplateCreate> }
  >({
    mutationFn: ({ id, update }) => csvImportApi.updateTemplate(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['csv-import', 'templates'],
      })
    },
  })
}

export function useDeleteCSVTemplate() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, number>({
    mutationFn: csvImportApi.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['csv-import', 'templates'],
      })
    },
  })
}

// ============================================================================
// Check Existing Data
// ============================================================================

export function useCheckExistingData() {
  return useMutation<
    ExistingDataCheckResponse,
    Error,
    { uploadId: string; targetTable: TargetTable; referenceYear: number }
  >({
    mutationFn: ({ uploadId, targetTable, referenceYear }) =>
      csvImportApi.checkExistingData(uploadId, targetTable, referenceYear),
  })
}

// ============================================================================
// Execute
// ============================================================================

export function useExecuteCSVImport() {
  const queryClient = useQueryClient()

  return useMutation<
    CSVImportResponse,
    Error,
    { uploadId: string; params: CSVImportExecuteParams; background?: boolean; force?: boolean }
  >({
    mutationFn: ({ uploadId, params, background, force }) =>
      csvImportApi.executeCSVImport(uploadId, params, background, force),
    onSuccess: (data) => {
      // Usage counters and imported-data listing change as soon as the job starts
      queryClient.invalidateQueries({ queryKey: ['csv-import', 'templates'] })
      queryClient.invalidateQueries({ queryKey: ['csv-import', 'data'] })
      if (data.status === 'completed') {
        // Invalidate entity and stats caches
        queryClient.invalidateQueries({ queryKey: ['admin', 'entities'] })
        queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
        queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] })
      }
    },
  })
}

// ============================================================================
// Imported Data
// ============================================================================

export function useCSVImportedData() {
  return useQuery<ImportedDatasetInfo[]>({
    queryKey: ['csv-import', 'data'],
    queryFn: csvImportApi.getImportedData,
    staleTime: 30 * 1000,
  })
}

export function useCSVImportedValues(
  dataSourceId: number | null,
  indicatorId: number | null,
  referenceYear?: number
) {
  return useQuery<ImportedValuesResponse>({
    queryKey: ['csv-import', 'data', dataSourceId, 'values', indicatorId, referenceYear ?? 'all'],
    queryFn: () =>
      csvImportApi.getImportedValues(dataSourceId!, indicatorId!, referenceYear),
    enabled: dataSourceId !== null && indicatorId !== null,
    staleTime: 30 * 1000,
  })
}

// ============================================================================
// Status
// ============================================================================

export function useCSVImportStatus(jobId: string | null, options?: { refetchInterval?: number }) {
  return useQuery<csvImportApi.CSVImportStatus>({
    queryKey: ['csv-import', 'status', jobId],
    queryFn: () => csvImportApi.getCSVImportStatus(jobId!),
    enabled: jobId !== null,
    refetchInterval: options?.refetchInterval ?? false,
  })
}
